/**
 * Renders an article body composed of structured bilingual blocks.
 */
import { ArrowUpLeft } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import NextArrow from "@/components/NextArrow";
import { pickLang, useLanguage } from "@/i18n/LanguageContext";

function Block({ block, lang }) {
  const text = pickLang(block.text, lang);
  if (block.type === "heading") {
    const Tag = block.level === 3 ? "h3" : "h2";
    const cls = block.level === 3 ? "sas-h3 mt-10" : "sas-h2 mt-12";
    return <Tag className={cls}>{text}</Tag>;
  }
  if (block.type === "paragraph") {
    return <p className="sas-body text-[17px] leading-[2] mt-5">{text}</p>;
  }
  if (block.type === "link") {
    const isExternal = /^https?:\/\//.test(block.href);
    const desc = pickLang(block.description, lang);
    return (
      <div className="mt-8 border-s-2 border-[color:var(--sas-accent)] ps-4">
        {isExternal ? (
          <a href={block.href} target="_blank" rel="noreferrer noopener" className="sas-btn-link text-base">
            {text} <ArrowUpLeft size={15} />
          </a>
        ) : (
          <LocalizedLink to={block.href} className="sas-btn-link text-base">
            {text} <NextArrow size={15} />
          </LocalizedLink>
        )}
        {desc && <p className="text-sm text-[color:var(--sas-ink-2)] mt-1.5">{desc}</p>}
      </div>
    );
  }
  if (block.type === "takeaway") {
    const label = lang === "ar" ? "الخلاصة" : "Key takeaway";
    return (
      <aside
        className="mt-10 rounded-xl border border-[color:var(--sas-line)] p-5 md:p-6"
        style={{ background: "var(--sas-accent-soft)" }}
        aria-label={label}
      >
        <div className="sas-eyebrow mb-2">{label}</div>
        <p className="text-[16px] leading-[1.9] text-[color:var(--sas-ink)] font-medium">{text}</p>
      </aside>
    );
  }
  return null;
}

export default function ArticleBody({ blocks = [] }) {
  const { lang } = useLanguage();
  return (
    <div data-testid="article-body">
      {blocks.map((b, i) => <Block key={i} block={b} lang={lang} />)}
    </div>
  );
}
